import { caliperCanvasSegments } from "./caliperGeometry";
import type { StoneFinding } from "./types";

const SIGNAL = "#4361ee";
const SIGNAL_TINT = "#93b4ff";
// Bone/stone window for CT calculi.
export const STONE_WINDOW = { windowWidth: 1500, windowCenter: 400 };

/**
 * Render the representative slice's crop for `stone` into a canvas, windowed for
 * bone, with long+short calipers and Ser·Slice / HU overlays drawn on top.
 * `loadFrame` returns the source slice as a canvas (reuse the wadouri frame-load
 * from useSeriesThumbnails.ts — extract loadS3PreviewFrame into a shared util
 * or pass an equivalent loader here). Throws on load failure (logged by caller).
 */
export async function renderStoneThumbnail(
  stone: StoneFinding,
  loadFrame: (
    seriesUid: string,
    sliceIndex: number,
    window: typeof STONE_WINDOW,
  ) => Promise<HTMLCanvasElement>,
  size = { w: 132, h: 132 },
): Promise<HTMLCanvasElement> {
  const src = await loadFrame(stone.seriesInstanceUid, stone.sliceIndex, STONE_WINDOW);
  const out = document.createElement("canvas");
  out.width = size.w;
  out.height = size.h;
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("2d context unavailable for stone thumbnail");
  const c = stone.cropBoundsPx;
  ctx.drawImage(src, c.x, c.y, c.w, c.h, 0, 0, size.w, size.h); // crop → fill

  const seg = caliperCanvasSegments(stone, size.w, size.h);
  ctx.lineWidth = 1.3;
  ctx.strokeStyle = SIGNAL;
  ctx.beginPath();
  ctx.moveTo(...seg.long[0]);
  ctx.lineTo(...seg.long[1]);
  ctx.stroke();
  ctx.strokeStyle = SIGNAL_TINT;
  ctx.setLineDash([2.5, 2]);
  ctx.beginPath();
  ctx.moveTo(...seg.short[0]);
  ctx.lineTo(...seg.short[1]);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.font = "8px 'JetBrains Mono', monospace";
  ctx.fillStyle = "rgba(255,255,255,0.62)";
  ctx.textBaseline = "top";
  ctx.fillText(`Ser ${stone.seriesNumber} · Sl ${stone.sliceNumber}`, 4, 4);
  ctx.textBaseline = "bottom";
  ctx.textAlign = "right";
  ctx.fillText(`HU ${stone.hu.max}`, size.w - 4, size.h - 4);
  return out;
}
